import { Inject, Injectable, InjectionToken, Provider } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanDeactivate,
  Resolve,
} from '@angular/router';
import { accountIdPathParam } from '@rivethealth/data/query-params';
import { syncValue } from '../rx/util';
import { SPLIT_FACTORY } from './split.service';

export const SPLIT_CLIENT = new InjectionToken<SplitIO.IClient>('SPLIT_CLIENT');

export function splitClient(route: ActivatedRoute) {
  return syncValue(route.data)!.splitClient;
}

export const SPLIT_CLIENT_PROVIDERS: Provider[] = [
  {
    provide: SPLIT_CLIENT,
    useFactory: splitClient,
    deps: [ActivatedRoute],
  },
];

function createSplitClient(
  splitFactory: SplitIO.ISDK,
  key: string,
  trafficType: string | undefined,
) {
  const client = splitFactory.client(key, trafficType);
  client.setMaxListeners(0);
  try {
    Promise.race([
      client.ready(),
      new Promise((resolve, reject) => {
        client.once(client.Event.SDK_READY, () => {
          resolve(null);
        });
        client.once(client.Event.SDK_READY_FROM_CACHE, () => {
          // If the user has loaded split values into the
          // localStorage cache this will resolve immediately,
          // effectively allowing us to ignore any
          // SDK_READY_TIMED_OUT errors and use (possibly stale)
          // cached values.
          resolve(null);
        });
        client.once(client.Event.SDK_READY_TIMED_OUT, () => {
          // the only time that this will actually reject is
          // if the user has never managed to get split values
          // into the localStorage cache - ie users who have
          // not been able to and continue to be unable to access
          // split
          reject('SDK_READY_TIMED_OUT');
        });
      }),
    ]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return client;
}

@Injectable({ providedIn: 'root' })
export class AccountSplitResolver implements Resolve<SplitIO.IClient> {
  constructor(
    @Inject(SPLIT_FACTORY) private readonly splitFactory: SplitIO.ISDK,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const accountId = route.paramMap.get(accountIdPathParam)!;
    const client = createSplitClient(this.splitFactory, accountId, 'account');
    return client;
  }
}

@Injectable({ providedIn: 'root' })
export class AdminSplitResolver implements Resolve<SplitIO.IClient> {
  constructor(
    @Inject(SPLIT_FACTORY) private readonly splitFactory: SplitIO.ISDK,
  ) {}

  resolve() {
    const client = createSplitClient(this.splitFactory, 'admin', 'admin');
    return client;
  }
}

@Injectable({ providedIn: 'root' })
export class DeveloperSplitResolver implements Resolve<SplitIO.IClient> {
  constructor(
    @Inject(SPLIT_FACTORY) private readonly splitFactory: SplitIO.ISDK,
  ) {}

  resolve() {
    const client = createSplitClient(
      this.splitFactory,
      'developer',
      'developer',
    );
    return client;
  }
}

@Injectable({ providedIn: 'root' })
export class SplitGuard implements CanDeactivate<any> {
  canDeactivate(component: any, route: ActivatedRouteSnapshot) {
    const client: SplitIO.IClient = route.data.splitClient;
    client.destroy();
    return true;
  }
}
